import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { EasybillService } from 'src/app/services/easybill.service';
import { NotificationService } from 'src/app/services/notification.service';
import {
  EasybillOrderSelection,
  EasybillSyncResult,
  EasybillSyncSummary,
  EasybillSyncType,
} from 'src/app/models/dialog-dto/easybill-sync.dto';

export interface DialogEasybillImportData {
  syncType: EasybillSyncType;
}

interface OrderItemState {
  assignTo: string;
  positions: { included: boolean; quantity: number }[];
}

const TITLE_KEYS: Record<EasybillSyncType, string> = {
  products: 'easybill.syncProducts',
  articles: 'easybill.syncArticles',
  companies: 'easybill.syncCompanies',
  orders: 'easybill.syncOrders',
};

@Component({
  selector: 'app-dialog-easybill-import',
  templateUrl: './dialog-easybill-import.component.html',
  styleUrl: './dialog-easybill-import.component.scss',
})
export class DialogEasybillImportComponent implements OnInit {
  loading = true;
  confirming = false;
  errorMessage = '';
  summary?: EasybillSyncSummary;

  selectedIds = new Set<string>();
  orderStates = new Map<string, OrderItemState>();

  constructor(
    public dialogRef: MatDialogRef<DialogEasybillImportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogEasybillImportData,
    private easybillService: EasybillService,
    private _notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.runDryRun();
  }

  get isOrders(): boolean {
    return this.data.syncType === 'orders';
  }

  get titleKey(): string {
    return TITLE_KEYS[this.data.syncType] ?? 'easybill.title';
  }

  private dryRun(): Observable<EasybillSyncSummary> {
    switch (this.data.syncType) {
      case 'products':
        return this.easybillService.dryRunProducts();
      case 'articles':
        return this.easybillService.dryRunArticles();
      case 'companies':
        return this.easybillService.dryRunCompanies();
      case 'orders':
        return this.easybillService.dryRunOrders();
    }
  }

  private confirmCall(selections: string[] | EasybillOrderSelection[]): Observable<EasybillSyncResult> {
    switch (this.data.syncType) {
      case 'products':
        return this.easybillService.confirmProducts(selections as string[]);
      case 'articles':
        return this.easybillService.confirmArticles(selections as string[]);
      case 'companies':
        return this.easybillService.confirmCompanies(selections as string[]);
      case 'orders':
        return this.easybillService.confirmOrders(selections as EasybillOrderSelection[]);
    }
  }

  runDryRun(): void {
    this.loading = true;
    this.errorMessage = '';
    this.dryRun().subscribe({
      next: (summary) => {
        this.summary = summary;
        this.selectedIds = new Set((summary.Changes ?? []).map((c) => c.EasybillID));
        this.orderStates.clear();
        if (this.isOrders) {
          (summary.Changes ?? []).forEach((item) => {
            this.orderStates.set(item.EasybillID, {
              // "Neue Lizenz anlegen" is the default per the original spec —
              // ExistingLicences are only a suggestion the admin opts into,
              // never pre-selected (a customer having *any* other licence
              // must not silently redirect an otherwise-independent new
              // order into it).
              assignTo: 'new',
              positions: (item.Positions ?? []).map((p) => ({ included: true, quantity: p.Quantity })),
            });
          });
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.error ?? 'easybill.syncFailed';
      },
    });
  }

  toggleItem(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  orderState(id: string): OrderItemState | undefined {
    return this.orderStates.get(id);
  }

  confirm(): void {
    if (this.confirming || !this.summary) {
      return;
    }
    this.confirming = true;
    this.errorMessage = '';

    let selections: string[] | EasybillOrderSelection[];
    if (this.isOrders) {
      const orderSelections: EasybillOrderSelection[] = [];
      (this.summary.Changes ?? []).forEach((item) => {
        if (!this.selectedIds.has(item.EasybillID)) {
          return;
        }
        const state = this.orderStates.get(item.EasybillID);
        if (!state) {
          return;
        }
        const positions = (item.Positions ?? [])
          .map((p, idx) => ({ position: p, selection: state.positions[idx] }))
          .filter(({ selection }) => selection?.included)
          .map(({ position, selection }) => ({ ProductID: position.ProductID, Quantity: selection.quantity }));

        orderSelections.push({
          EasybillID: item.EasybillID,
          AssignToLicenceGUID: state.assignTo !== 'new' ? state.assignTo : undefined,
          Positions: positions,
        });
      });
      selections = orderSelections;
    } else {
      selections = Array.from(this.selectedIds);
    }

    this.confirmCall(selections).subscribe({
      next: () => {
        this.confirming = false;
        this._notification.addMessage({ text: 'easybill.importSuccess' });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.confirming = false;
        this.errorMessage = err?.error?.error ?? 'easybill.importFailed';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
